import Entity from "./entity";
import Option from "./option";
import store from "@/store";

/**
 * Order in a given date with the information specific to that moment.
 *
 * Object representation for a Order document from the database.
 */
class Order extends Entity {
  constructor({
    id,
    restaurantId,
    date,
    hasBeenPaid,
    products,
    session,
    notifyToken
  } = {}) {
    super({ id: id, restaurantId: restaurantId });
    this.date = date;
    this.products = products;
    this.hasBeenPaid = hasBeenPaid ? hasBeenPaid : false;
    this.location = store.state.location;
    this.session = session ? session : store.state.session;
    this.notifyToken = notifyToken ? notifyToken : false;
  }

  get totalPrice() {
    let price = 0;
    this.products.forEach(product => {
      price += Number(product.price) ? Number(product.price) : 0;
      if (product.options) {
        product.options.forEach(option => (price += Number(option.addedPrice)));
      }
    });
    return price;
  }

  /**
   * @returns A plain JS object containing the fields as they will be saved to the
   * database.
   */
  _toMap() {
    let map = {
      id: this.id,
      date: this.date,
      type: this.type,
      data: this.data,
      hasBeenPaid: this.hasBeenPaid,
      location: this.location,
      session: this.session,
      products: this.products
        ? this.products.map(product => product.toMap())
        : [],
      notifyToken: this.notifyToken
    };
    return map;
  }

  /**
   * @param {*} map `Order` document data as a plain JS object
   * snapshot represented by this `Order`.
   *
   * @returns {Order} A new instance created from the provided map.
   */
  static _fromMap(map) {
    map.date = map.date
      ? map.date.toDate
        ? map.date.toDate()
        : map.date
      : null;
    if (map.products) {
      // An Order should always have at least one product

      map.products = map.products.map(OrderProduct.fromMap);
    }
    let order = new Order(map);
    return order;
  }

  /**
   * @returns {string} A `string` representation of the collection to which this
   * `Order` belongs to.
   */
  get _functionUrl() {
    return "orders";
  }

  get _entityName() {
    return "order";
  }
  /**
   * @returns {string} A `string` representation of the collection to which this
   * `LocationCode` belongs to.
   */
  get _collectionName() {
    return "orders";
  }
}

const OrderProductStatus = Object.freeze({
  pending: "pending",
  ready: "ready",
  preparing: "preparing",
  delivered: "delivered"
});

/**
 * Represents a `Product` that belongs to an `Order` in a specific moment.
 */
class OrderProduct {
  constructor({ id, comment, name, image, price, category, options } = {}) {
    this.id = id;
    this.comment = comment;
    this.name = name;
    this.image = image;
    this.price = price;
    this.category = category;
    this.options = options;
    this.status = OrderProductStatus.pending;
  }

  toMap() {
    return {
      id: this.id,
      comment: this.comment,
      name: this.name,
      image: this.image,
      price: this.price,
      category: this.category,
      options: this.options ? this.options.map(option => option.toMap()) : [],
      status: this.status
    };
  }

  static fromMap(product) {
    product.options = product.options
      ? product.options.map(Option.fromMap)
      : [];
    return new OrderProduct(product);
  }
}

export { Order, OrderProduct, OrderProductStatus };
